import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import NavigationSidebar from '../../components/navigation/NavigationSidebar';
import UserProfileDropdown from '../../components/navigation/UserProfileDropdown';
import NotificationCenter from '../../components/navigation/NotificationCenter';
import BreadcrumbNavigation from '../../components/navigation/BreadcrumbNavigation';
import GroupCard from './components/GroupCard';
import GroupCreationForm from './components/GroupCreationForm';
import GroupFilters from './components/GroupFilters';
import GroupStatistics from './components/GroupStatistics';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useLanguage } from '../../contexts/LanguageContext';
import { getGroups, deleteGroup } from '../../services/supabaseService';
import { useAuth } from '../../contexts/AuthContext';

const GroupManagement = () => {
  const navigate = useNavigate();
  const { currentLanguage, t } = useLanguage();
  const { profile, loading: authLoading } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [showCreationForm, setShowCreationForm] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    status: 'all',
    supervisor: 'all'
  });
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const data = await getGroups();
      setGroups(data || []);
    } catch (err) {
      console.error('Error loading groups:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async (id) => {
    if (window.confirm(t('groups.confirmDelete'))) {
      try {
        await deleteGroup(id);
        await loadGroups();
      } catch (err) {
        console.error('Error deleting group:', err);
        alert(t('groups.deleteError'));
      }
    }
  };

  const mockUser = profile || {
    name: currentLanguage === 'fr' ? 'Ahmed Benali' : 'أحمد بنعلي',
    role: 'agency_admin',
    agency: currentLanguage === 'fr' ? 'Agence Al-Baraka' : 'وكالة البركة',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg'
  };

  const mockNotifications = [];

  const mockSupervisors = [
  { id: 1, name: currentLanguage === 'fr' ? 'Mohammed El Fassi' : 'محمد الفاسي' },
  { id: 2, name: currentLanguage === 'fr' ? 'Fatima Zahra' : 'فاطمة الزهراء' },
  { id: 3, name: currentLanguage === 'fr' ? 'Youssef Alami' : 'يوسف العلمي' },
  { id: 4, name: currentLanguage === 'fr' ? 'Khadija Bennani' : 'خديجة بناني' }];


  const filteredGroups = groups?.filter((group) => {
    const matchesType = filterType === 'all' || group?.type === filterType;
    const matchesStatus = filterStatus === 'all' || group?.status === filterStatus;
    const matchesSearch = group?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    return matchesType && matchesStatus && matchesSearch;
  });

  const statistics = {
    totalGroups: groups?.length || 0,
    activeGroups: groups?.filter((g) => g?.assignedPilgrims < g?.capacity)?.length || 0,
    totalCapacity: groups?.reduce((sum, g) => sum + (g?.capacity || 0), 0) || 0,
    assignedPilgrims: groups?.reduce((sum, g) => sum + (g?.assignedPilgrims || 0), 0) || 0,
    availableSpots: groups?.reduce((sum, g) => sum + ((g?.capacity || 0) - (g?.assignedPilgrims || 0)), 0) || 0,
    totalRevenue: groups?.reduce((sum, g) => sum + (parseFloat(g?.revenue) || 0), 0) || 0
  };

  const labels = {
    fr: {
      title: 'Gestion des groupes',
      subtitle: 'Créez et gérez vos groupes de pèlerinage Omra et Hajj',
      createGroup: 'Créer un groupe',
      hideForm: 'Masquer le formulaire',
      noGroups: 'Aucun groupe trouvé',
      noGroupsDesc: 'Commencez par créer votre premier groupe de pèlerinage',
      exportData: 'Exporter les données',
      bulkActions: 'Actions groupées'
    },
    ar: {
      title: 'إدارة المجموعات',
      subtitle: 'إنشاء وإدارة مجموعات الحج والعمرة الخاصة بك',
      createGroup: 'إنشاء مجموعة',
      hideForm: 'إخفاء النموذج',
      noGroups: 'لم يتم العثور على مجموعات',
      noGroupsDesc: 'ابدأ بإنشاء مجموعة الحج الأولى الخاصة بك',
      exportData: 'تصدير البيانات',
      bulkActions: 'الإجراءات الجماعية'
    }
  };

  const l = labels?.[currentLanguage];

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => {
    setFilters({
      search: '',
      type: '',
      capacityStatus: '',
      month: '',
      nusukSync: ''
    });
  };

  const handleCreateGroup = async (groupData) => {
    console.log('Creating group:', groupData);
    setShowCreationForm(false);
  };

  const handleViewDetails = (group) => {
    console.log('View group details:', group);
    // Navigate to pilgrim management with group filter
    navigate(`/pilgrim-management?groupId=${group?.id}&groupName=${encodeURIComponent(group?.name)}`);
  };

  const handleAssignPilgrims = (group) => {
    console.log('Assign pilgrims to group:', group);
    // Navigate to pilgrim management to assign pilgrims
    navigate(`/pilgrim-management?action=assign&groupId=${group?.id}`);
  };

  const handleManageItinerary = (group) => {
    console.log('Manage group itinerary:', group);
    // Show alert for itinerary management
    alert(currentLanguage === 'fr' ? `Gestion de l'itinéraire pour ${group?.name}\n\nDépart: ${group?.departureDate}\nRetour: ${group?.returnDate}\nHôtel Makkah: ${group?.makkahHotel || group?.hotel || 'Non défini'}\nHôtel Madinah: ${group?.madinahHotel || 'Non défini'}\nVol: ${group?.flightNumber || 'Non défini'}`
      : `إدارة المسار لـ ${group?.name}\n\nالمغادرة: ${group?.departureDate}\nالعودة: ${group?.returnDate}\nفندق مكة: ${group?.makkahHotel || group?.hotel || 'غير محدد'}\nفندق المدينة: ${group?.madinahHotel || 'غير محدد'}\nرقم الرحلة: ${group?.flightNumber || 'غير محدد'}`);
  };

  const handleCommunicate = (group) => {
    console.log('Communicate with group:', group);
    // Navigate to communication center with group pre-selected
    navigate(`/communication-center?groupId=${group?.id}&groupName=${encodeURIComponent(group?.name)}`);
  };

  const handleLanguageChange = (language) => {
    console.log('Language changed to:', language);
    // Language change logic handled by LanguageContext
  };

  const handleLogout = () => {
    console.log('Logging out...');
  };

  const handleNotificationClick = (notification) => {
    console.log('Notification clicked:', notification);
  };

  const handleMarkAsRead = (notificationId) => {
    console.log('Mark notification as read:', notificationId);
  };

  return (
    <div className="min-h-screen bg-background">
      <NavigationSidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} />

      <div
        className={`transition-smooth ${
        isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-72'}`
        }>

        <header className="sticky top-0 z-30 bg-card border-b border-border shadow-warm">
          <div className="flex items-center justify-between gap-4 pl-16 pr-4 md:px-6 lg:px-8 py-4">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl font-bold text-foreground">
                {t('groups.title')}
              </h1>
              <p className="caption text-muted-foreground mt-1">{t('groups.subtitle')}</p>
            </div>
            <div className="flex items-center gap-3">
              <NotificationCenter
                notifications={mockNotifications}
                onNotificationClick={handleNotificationClick}
                onMarkAsRead={handleMarkAsRead}
                currentLanguage={currentLanguage} />

              <UserProfileDropdown
                user={mockUser}
                onLogout={handleLogout}
                onLanguageChange={handleLanguageChange}
                currentLanguage={currentLanguage} />

            </div>
          </div>
        </header>

        <main className="px-4 md:px-6 lg:px-8 py-6">
          <BreadcrumbNavigation currentLanguage={currentLanguage} />

          <GroupStatistics statistics={statistics} currentLanguage={currentLanguage} />

          <div className="flex flex-col lg:flex-row gap-6">
            <div className="flex-1 min-w-0 space-y-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <Button
                  variant={showCreationForm ? 'outline' : 'default'}
                  iconName={showCreationForm ? 'X' : 'Plus'}
                  iconPosition="left"
                  onClick={() => setShowCreationForm(!showCreationForm)}>

                  {showCreationForm ? t('groups.hideForm') : t('groups.createGroup')}
                </Button>
                <div className="flex gap-2">
                  <Button variant="outline" iconName="Download" iconPosition="left">
                    {t('groups.exportData')}
                  </Button>
                </div>
              </div>

              {showCreationForm && (
              <GroupCreationForm
                onCreateGroup={handleCreateGroup}
                supervisors={mockSupervisors}
                currentLanguage={currentLanguage}
                t={t} />
              )}

              {filteredGroups?.length === 0 ? (
              <div className="bg-card border border-border rounded-lg shadow-warm p-8 md:p-12 text-center">
                  <Icon name="Users" size={64} className="text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-foreground mb-2">{t('groups.noGroups')}</h3>
                  <p className="text-muted-foreground mb-6">{t('groups.noGroupsDesc')}</p>
                  <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => setShowCreationForm(true)}>

                    {t('groups.createGroup')}
                  </Button>
                </div>
              ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                  {filteredGroups?.map((group) => (
                <GroupCard
                  key={group?.id}
                  group={group}
                  onViewDetails={handleViewDetails}
                  onAssignPilgrims={handleAssignPilgrims}
                  onManageItinerary={handleManageItinerary}
                  onCommunicate={handleCommunicate}
                  currentLanguage={currentLanguage} />
                ))}
                </div>
              )}
            </div>

            <aside className="w-full lg:w-80 flex-shrink-0">
              <GroupFilters
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters}
                currentLanguage={currentLanguage} />

            </aside>
          </div>
        </main>
      </div>
    </div>);

};

export default GroupManagement;